import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { AiChatProvider } from './AiChatContext';
import AiChatShell from './AiChatShell';
import { resolveChatbotModuleId } from './resolveChatbotModuleId';
import { isAiChatDemoResponsesEnabled, isAiChatEnabledForPath } from './config';
import { useModuleEnableChatServerMap } from './hooks/useModuleEnableChatServerMap';
import { useChatbotModuleVisibility } from './hooks/useChatbotModuleVisibility';
import { resolveModuleChatEnabled } from './persistence/moduleEnableChatResolve';
import { resolveAiChatModuleKey } from './resolveAiChatModuleKey';
import { connectChatbotWs } from './ws/chatbotWsScope';
import './aiChat.scss';

export default function AiChatRoot({ projectId }) {
  const { pathname, search } = useLocation();
  const customizedModules = useSelector(
    (state) => state.projects?.customizedModules || [],
  );
  const chatbotModuleId = resolveChatbotModuleId(
    pathname,
    search,
    customizedModules,
  );
  const { serverModuleMap, loading: moduleMapLoading } =
    useModuleEnableChatServerMap(projectId);
  const moduleKey = resolveAiChatModuleKey(pathname, search);
  const { visible: visibilityVisible } =
    useChatbotModuleVisibility(chatbotModuleId, moduleKey);
  const providerKey = projectId ? `${projectId}:${moduleKey || 'nomodule'}` : '';
  const [wsConnectError, setWsConnectError] = useState(null);

  const chatEligible =
    Boolean(projectId) &&
    isAiChatEnabledForPath(pathname, search) &&
    resolveModuleChatEnabled(projectId, moduleKey, serverModuleMap) &&
    visibilityVisible &&
    !(Boolean(moduleKey) && moduleMapLoading);

  useEffect(() => {
    if (!chatEligible || isAiChatDemoResponsesEnabled()) {
      return undefined;
    }
    let cancelled = false;
    setWsConnectError(null);
    connectChatbotWs().catch((e) => {
      if (!cancelled) {
        const msg = e?.message || 'Could not connect to chat service.';
        setWsConnectError(typeof msg === 'string' ? msg : String(msg));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [chatEligible]);

  if (!chatEligible) {
    return null;
  }

  return (
    <AiChatProvider
      key={providerKey}
      projectId={projectId}
      modulePath={pathname}
      chatbotModuleId={chatbotModuleId}
      wsConnectError={wsConnectError}
    >
      <AiChatShell chatbotModuleId={chatbotModuleId} />
    </AiChatProvider>
  );
}
